import { DestinationItem, NestedProjectExplorer } from '@tableau/nested-project-explorer';
import { DropdownButton, DropdownButtonType } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { classes } from 'typestyle';

import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { projectPickerStyles } from '~src/projects/components/styles/ProjectPickerStyles';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isProjectSpace } from '~src/spacesPlatform/state/SpaceInfo';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../codegen/Localize';
import { UploadWorkbookStyles } from '../../styles/UploadWorkbookStyles';
import { getInitialProject, useProjects } from '../../utils/ProjectExplorerEffects';

export interface ProjectExplorerDropdownProps {
  testId: string;
  initialProjectId: string;
  handleProjectSelect: (project: DestinationItem) => void;
  dropdownLabel?: string;
}

export function InternalProjectExplorerDropdown(props: ProjectExplorerDropdownProps) {

  const projects = useProjects();
  const [selectedProject, setSelectedProject] = React.useState<DestinationItem>(null);
  // Note: We have to use a wrapper object around done() because we don't want this functional component to
  // re-render every time the dropdown pop-up opens as it gives a new done function
  const [closeDropdown] = React.useState<{ done: () => void }>({ done: null });

  React.useEffect(
    function setupInitialProject() {
      const initialProject = getInitialProject(projects, props.initialProjectId);
      setSelectedProject(initialProject);
      props.handleProjectSelect(initialProject);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, props.initialProjectId]
  );

  const handleChange = React.useCallback(
    (project: DestinationItem) => {
      setSelectedProject(project);
      props.handleProjectSelect(project);
      if (project) {
        closeDropdown.done();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeDropdown]
  );

  const assignDoneFunction = (done: () => void) => { closeDropdown.done = done; };

  return (
    <DropdownButton
      name="projectId"
      testId={props.testId}
      label={props.dropdownLabel ?? Messages.project()}
      className={`${UploadWorkbookStyles.textField} ${UploadWorkbookStyles.projectDropdown}`}
      children={selectedProject?.name}
      onOpen={assignDoneFunction}
      popupContent={
        <div
          className={classes(projectPickerStyles, UploadWorkbookStyles.projectExplorerPopup)}
        >
          {projects &&
            <NestedProjectExplorer
              topLevelDestinationStatus={'structurally_invalid'}
              projects={projects}
              onSelectedProjectChanged={handleChange}
              initiallySelectedProjectId={selectedProject?.id}
              selectRowOnExpansion={false}
              keepParentsOfSelectedItemExpanded={false}
              id='uploadWorkbookProjectExplorerId'
              style={{ maxWidth: 330 }}
            />
          }
        </div>
      }
      type={DropdownButtonType.Line}
      aria-haspopup={'tree'}
      aria-controls={'uploadWorkbookProjectExplorerId'}
    />
  );
}

const mapStateToProps: MapStateToProps<ProjectExplorerDropdownProps, ProjectExplorerDropdownProps> = (state: AppState, ownProps) => {
  const spaceInfo = selectSpaceInfo(state);
  const projectId = isProjectSpace(spaceInfo) ? spaceInfo.item.id : null;
  return {
    ...ownProps,
    initialProjectId: ownProps.initialProjectId ?? projectId
  };
};

export const ProjectExplorerDropdown = connect(mapStateToProps)(React.memo(InternalProjectExplorerDropdown, (prevProps: any, nextProps: any) => prevProps !== nextProps));
